/**
 * PwaInstallBanner — offers "install to home screen" on mobile after a few
 * visits. Adapts to iOS Safari (no beforeinstallprompt support).
 *
 * Visibility rules (all must be true):
 *   1. Viewport < 768px (mobile)
 *   2. App is not already running standalone
 *   3. User has visited ≥ 3 times on mobile
 *   4. The 7-day dismiss cooldown is not active
 *   5. The user is not flagged as already installed
 *
 * Sits on top of BottomNav (bottom-[80px]) so it never hides the nav.
 *
 * Telemetry: posts to /api/pwa/track when the banner is shown, when
 * install succeeds, when dismissed. The endpoint is fire-and-forget.
 */
import React, { useEffect, useState } from 'react';
import { X, Share, Plus } from 'lucide-react';

const KEY_VISITS    = 'compass_visit_count';
const KEY_DISMISSED = 'compass_install_dismissed';
const KEY_INSTALLED = 'compass_installed';
const DISMISS_DAYS  = 7;
const MIN_VISITS    = 3;

const isMobile = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
   window.navigator.standalone === true);

const detectIOS = () =>
  typeof window !== 'undefined' &&
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
  !window.MSStream;

const dismissActive = () => {
  const ts = parseInt(localStorage.getItem(KEY_DISMISSED) || '0', 10);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 86400_000;
};

// Best-effort analytics — talks to the same backend the rest of the app
// hits (REACT_APP_BACKEND_URL). Fails silently so a 500 here never
// breaks the install UX.
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const track = (event) => {
  try {
    fetch(`${API_BASE}/api/pwa/track`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
    }).catch(() => {});
  } catch (_) { /* noop */ }
};

const PwaInstallBanner = () => {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // SSR / standalone / desktop / already installed → never show.
    if (!isMobile() || isStandalone()) return;
    if (localStorage.getItem(KEY_INSTALLED) === 'true') return;

    // Increment visit counter on each mount.
    const visits = parseInt(localStorage.getItem(KEY_VISITS) || '0', 10) + 1;
    localStorage.setItem(KEY_VISITS, String(visits));
    if (visits < MIN_VISITS) return;
    if (dismissActive()) return;

    setIos(detectIOS());

    // Chrome / Edge / Android emit beforeinstallprompt with a prompt-fn.
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    // iOS never fires beforeinstallprompt — show the banner anyway with
    // the manual-instruction variant.
    if (detectIOS()) setVisible(true);

    // After a successful install, hide forever.
    const onInstalled = () => {
      localStorage.setItem(KEY_INSTALLED, 'true');
      setVisible(false);
      track('installed');
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      localStorage.setItem(KEY_INSTALLED, 'true');
      track('installed');
    } else {
      localStorage.setItem(KEY_DISMISSED, String(Date.now()));
      track('dismissed');
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(KEY_DISMISSED, String(Date.now()));
    track('dismissed');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Installer Compass"
      className="fixed left-4 right-4 z-[60] rounded-xl shadow-2xl border border-white/10 md:hidden"
      style={{
        bottom: `calc(80px + env(safe-area-inset-bottom, 0px))`,
        background: '#070A1E',
      }}
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Fermer"
        className="absolute top-2 right-2 p-1.5 text-white/40 hover:text-white/80 rounded-md"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-3 p-3 pr-9">
        <img
          src="/icons/icon-192.png"
          alt=""
          className="w-12 h-12 rounded-xl shrink-0"
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          {ios ? (
            <>
              <p className="text-[13px] text-white font-medium leading-snug">
                Installe Compass sur ton écran d'accueil
              </p>
              <p className="text-[11px] text-white/55 mt-0.5 flex items-center gap-1 flex-wrap">
                Tape sur
                <Share className="w-3 h-3 inline -mt-0.5 text-amber-500" />
                puis
                <Plus className="w-3 h-3 inline -mt-0.5 text-amber-500" />
                Sur l'écran d'accueil
              </p>
            </>
          ) : (
            <>
              <p className="text-[13px] text-white font-medium leading-snug">
                Installe Compass sur ton écran d'accueil
              </p>
              <p className="text-[11px] text-white/55 mt-0.5">
                Accès rapide, en plein écran
              </p>
            </>
          )}
        </div>
        {!ios && deferredPrompt && (
          <button
            type="button"
            onClick={handleInstall}
            className="tap-feedback shrink-0 px-3.5 py-2 rounded-full text-xs font-bold"
            style={{ background: '#C8A24A', color: '#070A1E' }}
          >
            Installer
          </button>
        )}
        {(ios || !deferredPrompt) && (
          <button
            type="button"
            onClick={handleDismiss}
            className="tap-feedback shrink-0 px-3 py-2 rounded-full text-xs font-semibold text-white/70"
          >
            Plus tard
          </button>
        )}
      </div>
    </div>
  );
};

export default PwaInstallBanner;
